import React, { useState } from "react";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Swal from "sweetalert2";

const YMRSList = ({ assessments, onAdd, onEdit, onDelete, isLoading, error }) => {
  const [filterText, setFilterText] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const records = assessments || [];

  const filteredRecords = records.filter((r) =>
    Object.values(r).some((v) => v != null && String(v).toLowerCase().includes(filterText.toLowerCase()))
  );

  const totalRows = filteredRecords.length;
  const start = (currentPage - 1) * rowsPerPage + 1;
  const end = Math.min(currentPage * rowsPerPage, totalRows);

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This YMRS assessment will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed && onDelete) {
        onDelete(id);
        Swal.fire("Deleted!", "YMRS assessment deleted successfully.", "success");
      }
    });
  };

  const handleExportPDF = () => {
    const doc = new jsPDF("l", "mm", "a4");
    doc.text("Young Mania Rating Scale (YMRS) Assessments", 14, 15);
    doc.setFontSize(10);

    const tableColumn = ["#", "Patient Name", "Date", "Total Score", "Assessed By"];
    const tableRows = filteredRecords.map((r, i) => [
      i + 1,
      r.patient_name || "",
      r.date || "",
      r.total_score || 0,
      r.assessed_by || ""
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 25,
      theme: "grid",
      headStyles: { fillColor: [0, 128, 0], textColor: 255 },
      styles: { fontSize: 9, cellPadding: 3 },
    });

    doc.save("ymrs_assessments.pdf");
  };

  const CustomPagination = ({ onChangePage, onChangeRowsPerPage, currentPage, rowsPerPage, rowCount }) => {
    const totalPages = Math.ceil(rowCount / rowsPerPage);
    return (
      <div className="d-flex justify-content-between align-items-center p-3 border-top">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted" style={{ fontSize: "0.875rem" }}>Rows per page:</span>
          <select
            className="form-select form-select-sm"
            style={{ width: "80px" }}
            value={rowsPerPage}
            onChange={(e) => onChangeRowsPerPage(Number(e.target.value), currentPage)}
          >
            {[5, 10, 15, 20].map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>

        <div className="d-flex align-items-center gap-1">
          <button className="btn btn-sm border" onClick={() => onChangePage(currentPage - 1, rowCount)} disabled={currentPage === 1}>
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
            Page {currentPage} of {totalPages}
          </span>
          <button className="btn btn-sm border" onClick={() => onChangePage(currentPage + 1, rowCount)} disabled={currentPage === totalPages}>
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    );
  };

  const columns = [
    { name: "S.No", cell: (_, index) => index + 1 + (currentPage - 1) * rowsPerPage, width: "80px" },
    { name: "Patient Name", selector: (row) => row.patient_name || "", sortable: true },
    { name: "Date", selector: (row) => row.date || "", sortable: true },
    { name: "Total Score", selector: (row) => row.total_score || 0, sortable: true, center: true },
    { name: "Assessed By", selector: (row) => row.assessed_by || "", sortable: true },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex justify-content-center gap-2">
          {onEdit && (
            <button className="btn btn-sm btn-outline-primary" onClick={() => onEdit(row.id)}>
              <i className="ri-edit-line"></i>
            </button>
          )}
          {onDelete && (
            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row.id)}>
              <i className="ri-delete-bin-line"></i>
            </button>
          )}
        </div>
      ),
      width: "130px",
      ignoreRowClick: true,
      button: true,
    },
  ];

  if (isLoading) {
    return <div className="p-5 text-center">Loading YMRS assessments...</div>;
  }

  if (error) {
    return <div className="p-5 text-center text-danger">{error}</div>;
  }

  return (
    <div>
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2 py-3">
        <div className="d-flex align-items-center gap-2">
          <div className="dropdown">
            <button className="btn btn-label-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown">
              <i className="ri-download-2-line me-1"></i> Export
            </button>
            <ul className="dropdown-menu shadow-sm">
              <li><button className="dropdown-item" onClick={handleExportPDF}><i className="ri-file-pdf-line me-1 text-danger"></i> PDF</button></li>
              <li>
                <CSVLink data={filteredRecords} filename="ymrs_assessments.csv" className="dropdown-item">
                  <i className="ri-file-excel-line me-1 text-success"></i> CSV
                </CSVLink>
              </li>
            </ul>
          </div>

          <input
            type="text"
            placeholder="Search YMRS assessments..."
            className="form-control form-control-sm"
            style={{ width: "300px" }}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        <h6 className="mb-0 text-center text-muted flex-grow-1">
          Showing {start}-{end} of {totalRows} records
        </h6>

        <div>
          {onAdd && (
            <button className="btn btn-sm btn-primary" onClick={onAdd}>
              <i className="ri-add-line me-1"></i> Add New
            </button>
          )}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredRecords}
        pagination
        paginationPerPage={rowsPerPage}
        paginationTotalRows={totalRows}
        onChangeRowsPerPage={setRowsPerPage}
        onChangePage={setCurrentPage}
        paginationComponent={CustomPagination}
        highlightOnHover
        striped
        responsive
        noDataComponent={
          <div className="p-5 text-center text-muted">
            <i className="ri-heart-pulse-line fs-1 d-block mb-3"></i>
            No YMRS assessments found
          </div>
        }
      />
    </div>
  );
};

export default YMRSList;
